import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Eyebrow, Reveal, Section } from "@/components/Primitives";
import { BookCallButton } from "@/context/BookCall";
import AgentRun from "@/components/AgentRun";
import {
    Principles,
    EngagementPath,
    RoiSection,
    CaseCard,
    CtaBanner,
    featuredClients,
} from "@/components/Blocks";
import { SITE } from "@/lib/data";

const HERO_LINES = [
    "AI systems that run",
    "inside your business,",
    "not on top of it.",
];

const ORG_JSONLD = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.entity,
    alternateName: "Phoebus AI",
    url: "https://phoebusai.com",
    email: SITE.email,
    logo: "https://phoebusai.com/phoebus-logo.png",
    description:
        "Phoebus AI builds and deploys autonomous AI agents and conversational systems engineered around a client's own workflows, data, and infrastructure.",
    address: {
        "@type": "PostalAddress",
        streetAddress:
            "02-067/271 & 296/219, Shirdi Hills, Gajularamaram Road",
        addressLocality: "Hyderabad",
        addressRegion: "Telangana",
        postalCode: "500055",
        addressCountry: "IN",
    },
    sameAs: [SITE.linkedin, SITE.facebook],
};

function Hero() {
    const reduce = useReducedMotion();
    return (
        <section className="relative px-5 sm:px-8 lg:px-16 pt-16 md:pt-24 pb-10 overflow-hidden">
            <div className="mx-auto max-w-[1180px] relative">
                <Reveal>
                    <Eyebrow>End-to-end AI automation · GCC delivery</Eyebrow>
                </Reveal>
                <h1 className="mt-6 font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl !leading-[1.15] max-w-4xl">
                    {HERO_LINES.map((line, i) => (
                        <span key={i} className="block overflow-hidden">
                            <motion.span
                                className="block"
                                initial={reduce ? false : { y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{
                                    duration: 0.7,
                                    delay: 0.1 + i * 0.12,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    className="mt-7 text-lg md:text-xl text-pa-ink-500 max-w-2xl leading-relaxed"
                    initial={reduce ? false : { opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                >
                    Phoebus AI builds and deploys autonomous agents and
                    conversational systems engineered around your own workflows,
                    data, and infrastructure — not a generic template.
                </motion.p>

                <motion.div
                    className="mt-9 flex flex-wrap items-center gap-4"
                    initial={reduce ? false : { opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.62 }}
                >
                    <BookCallButton testId="hero-book-call" />
                    <Link
                        to="/ai-agents"
                        className="group inline-flex items-center gap-2 px-6 py-3 border border-pa-blue-600 text-pa-blue-700 cut-tr hover:bg-pa-blue-050 transition-colors font-medium text-[15px]"
                        data-testid="hero-see-agents"
                    >
                        See how the agents work
                        <span className="transition-transform group-hover:translate-x-1">
                            →
                        </span>
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}

export default function Home() {
    const featured = featuredClients();
    return (
        <>
            <Seo
                title="AI systems that run inside your business"
                description="End-to-end AI agentic automation for GCC enterprises. Purpose-built models deployable inside your own private environment — with a named delivery record across the UAE, Qatar, and beyond."
                path="/"
                jsonLd={ORG_JSONLD}
            />
            <Hero />

            {/* Signature agent-run visualisation */}
            <Section className="pt-6 md:pt-8">
                <Reveal>
                    <Eyebrow>What an agent actually does</Eyebrow>
                    <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight max-w-2xl">
                        One inbound message, five steps, no human re-keying
                    </h2>
                </Reveal>
                <div className="mt-10">
                    <AgentRun />
                </div>
            </Section>

            {/* Differentiator — dark */}
            <Section dark>
                <div className="grid gap-10 md:grid-cols-[1fr_1fr] md:gap-16">
                    <Reveal>
                        <Eyebrow light>The difference</Eyebrow>
                        <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.08]">
                            Purpose-built models, deployed inside your own
                            environment
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="text-lg text-pa-blue-050/80 leading-relaxed">
                            Most AI vendors plug you into someone else's
                            ready-made model. Phoebus AI designs and fine-tunes
                            smaller, purpose-trained models around your specific
                            domain and data — lighter, faster, and more
                            predictable at scale.
                        </p>
                        <p className="mt-5 text-lg text-white font-medium leading-relaxed">
                            Deployable entirely within your own private
                            environment. Your sensitive data never has to route
                            through shared public APIs — data residency stays
                            inside your control.
                        </p>
                        <div className="mt-7">
                            <Link
                                to="/ai-agents"
                                className="group inline-flex items-center gap-2 font-mono text-sm text-pa-blue-050 hover:text-white transition-colors"
                            >
                                How we build and deploy
                                <span className="transition-transform group-hover:translate-x-1">
                                    →
                                </span>
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </Section>

            <Principles />
            <EngagementPath />
            <RoiSection />

            {/* Featured cases — lead with UAE + Qatar */}
            <Section>
                <Reveal>
                    <Eyebrow>Selected work</Eyebrow>
                    <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-xl">
                            A delivery record, not a pitch deck
                        </h2>
                        <Link
                            to="/work"
                            className="font-mono text-sm text-pa-blue-700 hover:text-pa-blue-600"
                            data-testid="home-all-work"
                        >
                            All case studies →
                        </Link>
                    </div>
                </Reveal>
                <div className="mt-12 grid gap-4 md:grid-cols-2">
                    {featured.map((c, i) => (
                        <CaseCard key={c.id} c={c} index={i} />
                    ))}
                </div>
            </Section>

            <CtaBanner />
        </>
    );
}
